import { useContext, useEffect, useState } from 'react';
import {
  ControlPopupContainer,
  ControlPopupContent,
  SwitchablePopupContainer,
} from './styles';
import { PopupMenu, PopupMenuItem } from './styles';
import { SelectionRange } from '../../EditablePage/EditableBlock';
import { Input, Switch, useId } from '@fluentui/react-components';
import { BitButton } from '../../../BitButton';
import { PronunciationEffect, useStore } from '../../../../store';
import { TrashSimple, X } from '@phosphor-icons/react';
import { TtsContext } from '../../../../contexts/TtsContext';

interface PronunciationPopupProps {
  blockId: string;
  selection?: SelectionRange | undefined;
  selectionId?: string;
}

export const PronunciationPopup = ({
  blockId,
  selection,
  selectionId,
}: PronunciationPopupProps) => {
  const { getBlock, createEffectSelection, removeEffectFromSelection } =
    useStore();
  const { hideToolbox } = useContext(TtsContext);

  const inputId = useId('input');

  const [interpretValue, setInterpretValue] = useState<string>();
  const [aliasValue, setAliasValue] = useState<string>('');

  const [isInterpret, setIsInterpret] = useState(false);

  useEffect(() => {
    const hasValue = getBlock(blockId)
      .selections.find((s) => s.id === selectionId)
      ?.controls.find((c) => c.control === 'pronunciation')?.value as
      | PronunciationEffect
      | undefined;

    if (hasValue) {
      if (hasValue.type === 'interpret') {
        setIsInterpret(true);
        setInterpretValue(hasValue.value);
        setAliasValue('');
      } else {
        setInterpretValue(undefined);
        setAliasValue(hasValue.value);
      }
    }
  }, [blockId, getBlock, selectionId]);

  const handleChange = (value: string | null) => {
    if (selection) {
      createEffectSelection(
        blockId,
        selection.start,
        selection.end,
        'pronunciation',
        value ? { type: isInterpret ? 'interpret' : 'alias', value } : ''
      );
    }
    hideToolbox();
  };

  const interpretOptions = [
    { value: 'address', label: 'Address' },
    { value: 'cardinal', label: 'Cardinal' },
    { value: 'characters', label: 'Characters (Spell-out)' },
    { value: 'datemdy', label: 'Date (m/d/y)' },
    { value: 'datedmy', label: 'Date (d/m/y)' },
    { value: 'digits', label: 'Digits' },
    { value: 'fraction', label: 'Fraction' },
    { value: 'ordinal', label: 'Ordinal' },
    { value: 'telephone', label: 'Telephone' },
    { value: 'time12', label: 'Time (12)' },
    { value: 'time24', label: 'Time (24)' },
    { value: 'name', label: 'Name' },
  ];

  return (
    <ControlPopupContainer>
      <ControlPopupContent>
        <SwitchablePopupContainer>
          <Switch
            style={{ alignItems: 'center' }}
            checked={isInterpret}
            onChange={(ev) => setIsInterpret(ev.currentTarget.checked)}
            label={
              isInterpret
                ? 'Speak as Configuration'
                : 'Substitutions Configuration'
            }
            labelPosition="above"
          />
          {isInterpret ? (
            <PopupMenu>
              {interpretOptions.map((option) => (
                <PopupMenuItem
                  isSelected={interpretValue === option.value}
                  key={crypto.randomUUID() + `speed-options-` + option.value}
                  onClick={() => {
                    setInterpretValue(option.value);
                    handleChange(option.value);
                  }}
                >
                  {option.label}
                </PopupMenuItem>
              ))}
            </PopupMenu>
          ) : (
            <div className="inputWrapper">
              <Input
                id={inputId}
                value={aliasValue}
                onChange={(_, data) => setAliasValue(data.value)}
              />
              <BitButton
                label="Apply"
                action={() => aliasValue && handleChange(aliasValue)}
              />
            </div>
          )}
        </SwitchablePopupContainer>
      </ControlPopupContent>
      {selectionId && (interpretValue || aliasValue) && (
        <PopupMenuItem
          isSelected={false}
          onClick={() => {
            removeEffectFromSelection(blockId, selectionId, 'pronunciation')
            hideToolbox()
          }
          }
          style={{ border: '1px solid #fd6262' }}
        >
          <X />
          Clear
        </PopupMenuItem>
      )}
    </ControlPopupContainer>
  );
};
